import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import Button from '../common/Button';
import { useSubscription } from '../../contexts/SubscriptionContext';
import subscriptionService from '../../services/subscriptionService';
import OrganizationService from '../../services/organizationService';
import './SubscriptionManagement.css';

const STRIPE_PRICE_MONTHLY = process.env.REACT_APP_STRIPE_PRICE_MONTHLY || 'price_1S4ROP2IC7KG0nTPNVGiaKsM';
const STRIPE_PRICE_YEARLY = process.env.REACT_APP_STRIPE_PRICE_YEARLY || 'price_1S4RNk2IC7KG0nTPljUdVHWB';
const DEFAULT_MONTHLY_PRICE_LABEL = '$99 MXN';
const DEFAULT_YEARLY_PRICE_LABEL = '$999 MXN';

const getStatusInfo = (status) => {
  const formatted = subscriptionService.formatSubscriptionStatus(status);
  return formatted || {
    text: status || 'No disponible',
    color: 'secondary',
    icon: 'fas fa-question-circle'
  };
};

const isNonNullObject = (value) => value !== null && typeof value === 'object';

const formatCardOnFile = (paymentMethod) => {
  if (!paymentMethod) return 'Sin tarjeta registrada';

  if (isNonNullObject(paymentMethod) && paymentMethod.card) {
    const brand = paymentMethod.card.brand ? paymentMethod.card.brand.toUpperCase() : 'Tarjeta';
    const last4 = paymentMethod.card.last4 ? ` terminada en ${paymentMethod.card.last4}` : '';
    return `${brand}${last4}`;
  }

  if (isNonNullObject(paymentMethod) && paymentMethod.object === 'card') {
    const brand = paymentMethod.brand ? paymentMethod.brand.toUpperCase() : 'Tarjeta';
    const last4 = paymentMethod.last4 ? ` terminada en ${paymentMethod.last4}` : '';
    return `${brand}${last4}`;
  }

  return 'Tarjeta registrada';
};

const formatAutoRenewal = (subscriptionObject) => {
  if (!subscriptionObject) return 'No disponible';
  return subscriptionObject.cancel_at_period_end ? 'Desactivada' : 'Activada';
};

const formatInvoiceStatus = (status) => {
  const statusMap = {
    paid: 'Pagado',
    open: 'Pendiente',
    draft: 'Borrador',
    void: 'Anulada',
    uncollectible: 'Incobrable'
  };

  return statusMap[status] || status || 'No disponible';
};

const isSuccessfulOrFailedInvoiceAttempt = (invoice) => {
  if (!invoice) return false;

  if (invoice.status === 'paid' || invoice.paid) {
    return true;
  }

  const hasFailedAttempt = invoice.attempted && !invoice.paid && (invoice.attempt_count || 0) > 0;
  return hasFailedAttempt;
};

const SubscriptionManagement = () => {
  const auth = useAuth();
  const { subscription, loading, reload, handlePaymentSuccess } = useSubscription();
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [organizationPricing, setOrganizationPricing] = useState(null);
  const [organizationPricingLoading, setOrganizationPricingLoading] = useState(true);

  const loadSubscription = useCallback(async () => {
    // Use the context reload function instead
    reload();
  }, [reload]);

  // Load subscription data
  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const loadOrganizationPricing = useCallback(async () => {
    const token = auth.user?.access_token;
    const userId = auth.user?.profile?.sub;

    if (!token || !userId) {
      setOrganizationPricingLoading(false);
      return;
    }

    try {
      setOrganizationPricingLoading(true);
      const organization = await OrganizationService.getCurrentUserOrganization(userId, token);
      if (!organization) {
        return;
      }

      setOrganizationPricing({
        monthlyPriceId: organization.subscriptionPriceMonthlyId,
        yearlyPriceId: organization.subscriptionPriceYearlyId,
        monthlyPriceLabel: organization.subscriptionPriceMonthlyLabel,
        yearlyPriceLabel: organization.subscriptionPriceYearlyLabel
      });
    } catch (error) {
      console.error('Error loading organization subscription pricing:', error);
    } finally {
      setOrganizationPricingLoading(false);
    }
  }, [auth.user?.access_token, auth.user?.profile?.sub]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      loadOrganizationPricing();
    }
  }, [auth.isAuthenticated, loadOrganizationPricing]);

  // Check for successful checkout session
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setMessage({
        type: 'success',
        text: '¡Suscripción activada exitosamente!'
      });
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Handle payment success with cache invalidation
      handlePaymentSuccess().then(() => {
        console.log('Subscription cache invalidated after payment success');
      }).catch((error) => {
        console.error('Error invalidating cache after payment:', error);
        // Fallback to regular reload
        setTimeout(() => {
          loadSubscription();
        }, 1000);
      });
    }
  }, [handlePaymentSuccess, loadSubscription]);

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      const token = auth.user?.access_token;
      const portalSession = await subscriptionService.createPortalSession(token);
      subscriptionService.redirectToPortal(portalSession.url);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al acceder al portal de facturación'
      });
      setActionLoading(false);
    }
  };

  const renderSubscriptionStatus = () => {
    if (!subscription) return null;

    const statusInfo = getStatusInfo(subscription.status);
    const daysRemaining = subscription.subscriptionEndDate ? 
      subscriptionService.calculateDaysRemaining(subscription.subscriptionEndDate) : 0;

    return (
      <div className={`subscription-status status-${statusInfo.color}`}>
        <div className="status-icon">
          <i className={statusInfo.icon}></i>
        </div>
        <div className="status-info">
          <h4>Estado de Suscripción</h4>
          <p className="status-text">{statusInfo.text}</p>
          {subscription.status === 'trial' && (
            <p className="trial-info">
              <strong>{daysRemaining} días restantes</strong> de período de prueba
            </p>
          )}
          {subscription.subscriptionEndDate && subscription.status !== 'trial' && (
            <p className="subscription-info">
              Vigente hasta: {subscriptionService.formatISODate(subscription.subscriptionEndDate)}
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderBillingSummary = () => {
    if (!subscription?.customer) return null;

    const stripeSubscription = subscription.subscription;
    const subscriptionItem = stripeSubscription?.items?.data?.[0];
    const price = subscriptionItem?.price;
    const product = typeof price?.product === 'object' ? price.product : null;
    const latestInvoice = typeof stripeSubscription?.latest_invoice === 'object' ? stripeSubscription.latest_invoice : null;
    const customerDefaultPaymentMethod = isNonNullObject(subscription?.customer?.invoice_settings?.default_payment_method)
      ? subscription.customer.invoice_settings.default_payment_method
      : null;
    const customerDefaultSource = isNonNullObject(subscription?.customer?.default_source)
      ? subscription.customer.default_source
      : null;
    const listedPaymentMethod = Array.isArray(subscription?.paymentMethods)
      ? subscription.paymentMethods.find((paymentMethod) => paymentMethod?.card || paymentMethod?.object === 'card')
      : null;
    const defaultPaymentMethod = isNonNullObject(stripeSubscription?.default_payment_method)
      ? stripeSubscription.default_payment_method
      : customerDefaultPaymentMethod || customerDefaultSource || listedPaymentMethod;

    const lastPaymentTimestamp = latestInvoice?.status_transitions?.paid_at
      || (latestInvoice?.status === 'paid' ? latestInvoice?.created : null);

    const billingSummaryItems = [
      {
        label: 'Renovación automática',
        value: formatAutoRenewal(stripeSubscription)
      },
      {
        label: 'Último pago',
        value: lastPaymentTimestamp ? subscriptionService.formatDate(lastPaymentTimestamp) : 'No disponible'
      },
      {
        label: 'Tarjeta registrada',
        value: formatCardOnFile(defaultPaymentMethod)
      },
      {
        label: 'Plan actual',
        value: product?.name || price?.nickname || 'No disponible'
      }
    ];

    return (
      <div className="billing-summary">
        <h4>Resumen de facturación</h4>
        <div className="billing-summary-grid">
          {billingSummaryItems.map((item) => (
            <div key={item.label} className="billing-summary-item">
              <span className="billing-summary-label">{item.label}</span>
              <span className="billing-summary-value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPaymentHistory = () => {
    const invoices = Array.isArray(subscription?.invoices)
      ? subscription.invoices.filter(isSuccessfulOrFailedInvoiceAttempt)
      : [];

    if (!subscription?.customer || invoices.length === 0) {
      return null;
    }

    return (
      <div className="subscription-card payment-history-card">
        <div className="payment-history-header">
          <h4>Historial de pagos</h4>
          <span>{invoices.length} pagos registrados</span>
        </div>

        <div className="payment-history-list">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="payment-history-item">
              <div className="payment-history-main">
                <span className="payment-history-date">
                  {subscriptionService.formatDate(invoice.status_transitions?.paid_at || invoice.created)}
                </span>
                <span className={`payment-history-status status-${invoice.status}`}>
                  {formatInvoiceStatus(invoice.status)}
                </span>
              </div>

              <div className="payment-history-meta">
                <span className="payment-history-amount">
                  {subscriptionService.formatPrice(invoice.amount_paid ?? invoice.total, invoice.currency || 'mxn')}
                </span>
                {invoice.invoice_pdf ? (
                  <a
                    className="payment-history-receipt-link"
                    href={invoice.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Descargar recibo PDF
                  </a>
                ) : (
                  <span className="payment-history-receipt-link is-disabled">Recibo no disponible</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRefunds = () => {
    const refunds = Array.isArray(subscription?.refunds)
      ? subscription.refunds
      : [];

    if (!subscription?.customer || refunds.length === 0) {
      return null;
    }

    return (
      <div className="subscription-card refunds-card">
        <div className="refunds-header">
          <h4>Reembolsos</h4>
          <span>{refunds.length} reembolso{refunds.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="refunds-list">
          {refunds.map((refund) => (
            <div key={refund.id} className="refund-item">
              <div className="refund-main">
                <span className="refund-date">
                  {subscriptionService.formatDate(refund.created)}
                </span>
                <span className="refund-status status-paid">
                  Completado
                </span>
              </div>

              <div className="refund-meta">
                <span className="refund-amount">
                  {subscriptionService.formatPrice(refund.amount, refund.currency || 'mxn')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleSubscribe = async (priceId) => {
    try {
      setActionLoading(true);
      setMessage({ type: '', text: '' });
      
      const token = auth.user?.access_token;
      const successUrl = `${window.location.origin}/subscription/thank-you?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/settings/subscription`;
      
      // The service will handle the redirect to Stripe Checkout
      await subscriptionService.createCheckoutSession(priceId, token, successUrl, cancelUrl);
      
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error al crear la sesión de pago. Inténtalo de nuevo.'
      });
      setActionLoading(false);
    }
  };

  const renderPricingPlans = () => {
    const pricing = {
      monthlyPriceId: organizationPricing?.monthlyPriceId || STRIPE_PRICE_MONTHLY,
      yearlyPriceId: organizationPricing?.yearlyPriceId || STRIPE_PRICE_YEARLY,
      monthlyPriceLabel: organizationPricing?.monthlyPriceLabel || DEFAULT_MONTHLY_PRICE_LABEL,
      yearlyPriceLabel: organizationPricing?.yearlyPriceLabel || DEFAULT_YEARLY_PRICE_LABEL
    };

    const isCustomPlan = Boolean(
      organizationPricing?.monthlyPriceId ||
      organizationPricing?.yearlyPriceId ||
      organizationPricing?.monthlyPriceLabel ||
      organizationPricing?.yearlyPriceLabel
    );

    const plans = [
      {
        id: pricing.monthlyPriceId,
        name: 'Plan Fundadores',
        price: pricing.monthlyPriceLabel,
        interval: 'mes',
        badge: '¡Cupos Limitados!',
        features: [
          'Precio especial (Regular $169)',
          'Precio garantizado x 2 años',
          'Gestión completa de pacientes',
          'Agenda de citas',
          'Historial médico',
          'Reportes básicos'
        ]
      },
      {
        id: pricing.yearlyPriceId,
        name: 'Plan Anual',
        price: pricing.yearlyPriceLabel,
        interval: 'año',
        badge: 'Ahorra 17%',
        features: [
          'Precio especial (Regular $1699)',
          'Gestión completa de pacientes',
          'Agenda de citas',
          'Historial médico',
          'Reportes básicos',
          'Soporte técnico estándar'
        ]
      }
    ];

    return (
      <div className="pricing-plans-container">
        <h4>Planes Disponibles</h4>
        <p>Elige el plan que mejor se adapte a tus necesidades:</p>
        
        <div className="pricing-plans-grid">
          {plans.map((plan) => (
            <div key={plan.id} className="pricing-plan">
              {!isCustomPlan && plan.badge && (
                <div className="plan-badge">{plan.badge}</div>
              )}
              <div className="plan-header">
                {isCustomPlan ? (
                  <>
                    <h5>Plan personalizado para tu consultorio</h5>
                    <div className="plan-price">
                      <span className="price">{plan.price}</span>
                      <span className="interval">/ {plan.interval === 'mes' ? 'Mensual' : 'Anual'}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <h5>{plan.name}</h5>
                    <div className="plan-price">
                      <span className="price">{plan.price}</span>
                      <span className="interval">/ {plan.interval}</span>
                    </div>
                  </>
                )}
              </div>
              {!isCustomPlan && (
                <div className="plan-features">
                  <ul>
                    {plan.features.map((feature, index) => (
                      <li key={index}>
                        <i className="fas fa-check"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="plan-action">
                <Button
                  variant="primary"
                  onClick={() => handleSubscribe(plan.id)}
                  loading={actionLoading}
                  className="subscribe-btn"
                >
                  Suscribirse
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPricingTable = () => {
    return renderPricingPlans();
  };

  if (loading || organizationPricingLoading) {
    return (
      <div className="subscription-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const canManageSubscription = Boolean(subscription?.customer) && !['cancelled', 'no_subscription', 'inactive'].includes(subscription?.status);

  return (
    <div className="subscription-management">
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="subscription-card">
        {renderSubscriptionStatus()}
      </div>

      {canManageSubscription && (
        <div className="subscription-card">
          {renderBillingSummary()}

          <div className="subscription-actions">
            <Button
              variant="primary"
              icon="fas fa-cog"
              onClick={handleManageSubscription}
              loading={actionLoading}
            >
              Gestionar Suscripción
            </Button>
          </div>
        </div>
      )}

      {renderPaymentHistory()}

      {renderRefunds()}

      {subscription?.status === 'trial' && (
        <div className="subscription-section trial-subscription">
          <div className="trial-content">
            <i className="fas fa-gift"></i>
            <h4>¡Aprovecha tu período de prueba!</h4>
            <p>
              Tienes <strong>{subscriptionService.calculateDaysRemaining(subscription.subscriptionEndDate)} días restantes</strong> para 
              explorar todas las funcionalidades de Dentaris de forma gratuita.
            </p>
            <p>
              Termina el {subscriptionService.formatISODate(subscription.subscriptionEndDate)}
            </p>
            {subscriptionService.calculateDaysRemaining(subscription.subscriptionEndDate) > 0 && (
              <div className="trial-benefit-notice">
                <span>
                  <strong>¡Ventaja especial!</strong> Al suscribirte ahora, conservarás tus {subscriptionService.calculateDaysRemaining(subscription.subscriptionEndDate)} días restantes 
                  sin cargo adicional.
                </span>
              </div>
            )}
          </div>
          
          <div className="trial-upgrade-section">
            <h5>¿Te gusta lo que ves? ¡Actualiza ahora!</h5>
            <p>Mantén el acceso completo a todas las funcionalidades sin interrupciones.</p>
            {renderPricingTable()}
          </div>
        </div>
      )}

      {(subscription?.status === 'no_subscription' || subscription?.status === 'inactive') && (
        <div className="subscription-section no-subscription">
          <div className="no-subscription-content">
            <i className="fas fa-crown"></i>
            <h4>Activa tu suscripción</h4>
            <p>Obtén acceso completo a todas las funcionalidades de Dentaris</p>
          </div>
          
          {renderPricingTable()}
        </div>
      )}

      {subscription?.status === 'cancelled' && (
        <div className="subscription-section cancelled-subscription">
          <div className="cancelled-content">
            <i className="fas fa-info-circle"></i>
            <h4>Suscripción Cancelada</h4>
            <p>Tu suscripción ha sido cancelada. Puedes reactivarla en cualquier momento.</p>
          </div>
          
          {renderPricingTable()}
        </div>
      )}

      {subscription?.status === 'past_due' && (
        <div className="subscription-section past-due-subscription">
          <div className="past-due-content">
            <i className="fas fa-exclamation-triangle"></i>
            <h4>Pago Pendiente</h4>
            <p>Tu suscripción tiene un pago pendiente. Actualiza tu método de pago para continuar.</p>
          </div>
          
          <div className="subscription-actions">
            <Button
              variant="primary"
              icon="fas fa-credit-card"
              onClick={handleManageSubscription}
              loading={actionLoading}
            >
              Actualizar Método de Pago
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
